export interface LegacyHeaderConfigLike {
  [key: string]: any;
}

export function isLegacyHeaderConfig(value: unknown): value is LegacyHeaderConfigLike {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const candidate = value as LegacyHeaderConfigLike;
  return Array.isArray(candidate.menu_items)
    && (typeof candidate.bg_color === 'string' || typeof candidate.height === 'number');
}

function mobileMode(position: unknown): string {
  if (position === 'right') return 'drawer-right';
  if (position === 'top') return 'top';
  if (position === 'fullscreen') return 'fullscreen';
  return 'drawer-left';
}

/**
 * Converts the old Karta Wygląd header payload into ordinary builder blocks.
 * The original payload is retained in globals for audit/round-tripping, while
 * every active visual option is mapped onto editable row/logo/nav/action blocks.
 */
export function convertLegacyHeaderToBlocks(raw: LegacyHeaderConfigLike): any[] {
  const widths = raw.column_widths || {};
  const logoWidth = Number(widths.logo ?? 20);
  const menuWidth = Number(widths.menu ?? 55);
  const iconsWidth = Number(widths.icons ?? 25);
  const widthSum = logoWidth + menuWidth + iconsWidth || 100;
  const normalizedWidths = [logoWidth, menuWidth, iconsWidth].map((value) => Number(((value / widthSum) * 100).toFixed(2)));
  normalizedWidths[2] = Number((100 - normalizedWidths[0] - normalizedWidths[1]).toFixed(2));
  const fontFamily = raw.font_family || "'Onest Variable', sans-serif";
  const textColor = raw.text_color || '#171717';
  const backgroundColor = raw.bg_color || '#ffffff';
  const borderVisible = raw.border_style && raw.border_style !== 'none' && Number(raw.border_width || 0) > 0;

  const content: any[] = [
    {
      id: 'globals',
      type: 'globals',
      settings: {
        fonts: [fontFamily, fontFamily],
        colors: [backgroundColor, textColor, '#22c55e', '#f59e0b', '#ffffff'],
        sticky: raw.sticky === true,
        stickyMode: raw.sticky === true ? 'always' : 'none',
        stickyOffset: 0,
        zIndex: 60,
        legacyHeaderConfig: raw,
      },
    },
  ];

  if (raw.topbar_enabled) {
    content.push({
      id: 'legacy-topbar-row',
      type: 'row',
      customName: 'Pasek ogłoszeń',
      styles: {
        fullWidth: false,
        backgroundColor: raw.topbar_bg_color || textColor,
        color: raw.topbar_text_color || '#ffffff',
        minHeight: Number(raw.topbar_height || 36),
        paddingTop: 6,
        paddingBottom: 6,
      },
      settings: { columns: 1, alignItems: 'center', justifyContent: raw.topbar_alignment || 'center' },
      children: [{
        id: 'legacy-topbar-column',
        type: 'container',
        settings: {},
        styles: {},
        children: [{
          id: 'legacy-topbar-text',
          type: 'text',
          content: raw.topbar_link
            ? `<a href="${raw.topbar_link}">${raw.topbar_content || ''}</a>`
            : String(raw.topbar_content || ''),
          settings: {},
          styles: {
            color: raw.topbar_text_color || '#ffffff',
            textAlign: raw.topbar_alignment === 'left' ? 'left' : raw.topbar_alignment === 'right' ? 'right' : 'center',
            fontSize: Number(raw.topbar_font_size || 13),
            fontWeight: raw.topbar_font_weight || 700,
          },
        }],
      }],
    });
  }

  content.push({
    id: 'legacy-main-header-row',
    type: 'row',
    customName: 'Nagłówek Kidlox — logo · menu · ikony',
    styles: {
      fullWidth: false,
      backgroundColor,
      color: textColor,
      minHeight: Number(raw.height || 80),
      borderWidth: borderVisible ? Number(raw.border_width || 1) : 0,
      borderColor: raw.border_color || 'rgba(0,0,0,0.08)',
      shadowEnabled: raw.border_shadow === true,
      shadowOffsetX: 0,
      shadowOffsetY: Math.max(1, Math.round(Number(raw.border_shadow_size || 8) / 2)),
      shadowBlur: Number(raw.border_shadow_size || 8),
      shadowSpread: 0,
      shadowColor: raw.border_shadow_color || 'rgba(0,0,0,0.08)',
      fontFamily,
      desktop: {
        fullWidth: false,
        minHeight: Number(raw.height || 80),
        gap: 0,
        alignItems: 'center',
      },
      mobile: {
        minHeight: Number(raw.height_mobile || raw.height || 72),
        gap: 4,
        alignItems: 'center',
      },
    },
    settings: {
      columns: 3,
      columnWidths: normalizedWidths,
      alignItems: 'center',
      justifyContent: 'between',
      mobileLayout: 'header-split',
      mobileColumns: 3,
      mobileOrder: { 0: 0, 1: 2, 2: 1 },
      centerMiddleColumn: true,
    },
    children: [
      {
        id: 'legacy-logo-column',
        type: 'container',
        customName: 'Logo',
        styles: {},
        settings: {},
        children: [{
          id: 'legacy-store-logo',
          type: 'store-logo',
          customName: 'Logo Kidlox',
          content: '',
          styles: {
            backgroundColor: 'transparent',
            color: textColor,
            paddingLeft: Number(raw.logo_padding_x || 0),
            paddingRight: Number(raw.logo_padding_x || 0),
            paddingTop: Number(raw.logo_padding_y || 0),
            paddingBottom: Number(raw.logo_padding_y || 0),
          },
          settings: {
            href: '/',
            logoUrl: raw.logo_url || undefined,
            alignmentDesktop: 'left',
            alignmentTablet: 'left',
            alignmentMobile: 'center',
            heightDesktop: Number(raw.logo_max_height || 64),
            heightTablet: Number(raw.logo_max_height_mobile || raw.logo_max_height || 52),
            heightMobile: Number(raw.logo_max_height_mobile || 46),
            maxWidthDesktop: Number(raw.logo_max_width || 180),
            maxWidthTablet: Number(raw.logo_max_width || 180),
            maxWidthMobile: Number(raw.logo_max_width || 170),
          },
        }],
      },
      {
        id: 'legacy-navigation-column',
        type: 'container',
        customName: 'Menu',
        styles: {},
        settings: {},
        children: [{
          id: 'legacy-store-navigation',
          type: 'store-navigation',
          customName: 'Menu Kidlox',
          content: '',
          styles: {
            backgroundColor: 'transparent',
            color: textColor,
            fontFamily,
          },
          settings: {
            source: 'manual',
            items: Array.isArray(raw.menu_items)
              ? raw.menu_items.map((item: any) => item?.image_url
                ? { ...item, image_floating: item.image_floating !== false }
                : item)
              : [],
            direction: 'horizontal',
            alignmentDesktop: 'center',
            alignmentTablet: 'center',
            alignmentMobile: 'left',
            gapDesktop: 0,
            gapTablet: 0,
            fontFamily,
            fontSize: Number(raw.menu_font_size || 14),
            fontWeight: raw.menu_font_weight || '600',
            letterSpacing: raw.menu_letter_spacing || undefined,
            offsetYDesktop: Number(raw.menu_y_offset || 0),
            verticalAlignment: raw.menu_valign || 'center',
            paddingX: Number(raw.menu_item_padding_x ?? 10),
            paddingY: Number(raw.menu_item_padding_y ?? 8),
            itemBorderRadius: Number(raw.menu_item_border_radius ?? 8),
            hoverColor: raw.menu_hover_color || textColor,
            hoverBg: raw.menu_hover_bg || 'transparent',
            activeColor: raw.menu_active_color || textColor,
            activeBg: raw.menu_active_bg || 'transparent',
            dropdownBg: raw.dropdown_bg || '#ffffff',
            dropdownTextColor: raw.dropdown_text_color || textColor,
            dropdownHoverBg: raw.dropdown_hover_bg || '#f4f4f5',
            dropdownBorderRadius: Number(raw.dropdown_border_radius ?? 12),
            dropdownShadow: raw.dropdown_shadow !== false,
            headerHeight: Number(raw.height || 80),
            mobileMode: mobileMode(raw.mobile_menu_position),
            mobileTopFromViewport: raw.mobile_menu_position === 'top',
            mobileTriggerBorder: false,
            iconSizeDesktop: Number(raw.icon_size_desktop || 22),
            iconSizeMobile: Number(raw.icon_size_mobile || 22),
            menuIconSource: raw.hamburger_icon_url ? 'custom' : 'system',
            menuIconUrl: raw.hamburger_icon_url || undefined,
            showMobileLabel: false,
            mobileLabel: 'Menu',
            mobileTitle: 'Menu',
            mobileBg: raw.mobile_menu_bg_color || backgroundColor,
            mobileTextColor: raw.mobile_menu_text_color || textColor,
            mobileFontFamily: raw.mobile_menu_font_family || fontFamily,
            mobileFontSize: Number(raw.mobile_menu_font_size || 16),
            mobileFontWeight: raw.mobile_menu_font_weight || '600',
            mobileAlignment: raw.mobile_menu_alignment || 'left',
            mobileItemGap: Number(raw.mobile_menu_item_spacing ?? 4),
            mobilePanelPadding: 20,
            mobilePanelWidth: 86,
          },
        }],
      },
      {
        id: 'legacy-actions-column',
        type: 'container',
        customName: 'Konto i koszyk',
        styles: {},
        settings: {},
        children: [{
          id: 'legacy-store-actions',
          type: 'store-actions',
          customName: 'Ikony Kidlox',
          content: '',
          styles: { backgroundColor: 'transparent', color: textColor },
          settings: {
            iconSource: raw.account_icon_url || raw.cart_icon_url ? 'custom' : 'theme',
            accountIconSource: raw.account_icon_url ? 'custom' : 'system',
            cartIconSource: raw.cart_icon_url ? 'custom' : 'system',
            accountIconUrl: raw.account_icon_url || undefined,
            cartIconUrl: raw.cart_icon_url || undefined,
            order: Array.isArray(raw.icon_order) ? raw.icon_order : ['language', 'currency', 'account', 'cart'],
            alignmentDesktop: 'right',
            alignmentTablet: 'right',
            alignmentMobile: 'right',
            gapDesktop: 8,
            gapTablet: 6,
            gapMobile: 4,
            iconSizeDesktop: Number(raw.icon_size_desktop || 22),
            iconSizeTablet: Number(raw.icon_size_mobile || raw.icon_size_desktop || 22),
            iconSizeMobile: Number(raw.icon_size_mobile || 22),
            showLabels: false,
            showLanguage: raw.show_language_selector === true,
            showCurrency: raw.show_currency_selector === true,
            showAccount: true,
            showCart: true,
          },
        }],
      },
    ],
  });

  return content;
}
